<template>
  <div class="student-analytics">
    <!-- Analytics Header -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h4 class="fw-bold">Student Analytics</h4>
      <div class="d-flex gap-2">
        <select class="form-select" style="width: auto;" v-model="selectedTimeframe" @change="reloadAll">
          <option value="week">This Week</option>
          <option value="month">This Month</option>
          <option value="quarter">This Quarter</option>
          <option value="year">This Year</option>
        </select>
        <select class="form-select" style="width: auto;" v-model="selectedClass" @change="reloadAll">
          <option value="">All Classes</option>
          <option v-for="c in teacherClasses" :key="c.class_code" :value="c.class_code">
            {{ c.class_code }} - {{ c.subject_name }}
          </option>
        </select>
        <button class="btn btn-outline-primary" @click="exportReport">
          <i class="bi bi-download me-2"></i>Export Report
        </button>
      </div>
    </div>

    <!-- Overview Cards -->
    <div class="row mb-4">
      <div class="col-md-3">
        <div class="modern-card p-3 text-center">
          <div class="metric-icon mb-2">
            <i class="bi bi-people-fill text-primary" style="font-size: 2rem;"></i>
          </div>
          <h3 class="fw-bold text-primary">{{ totalStudents }}</h3>
          <p class="text-muted mb-0">Total Students</p>
        </div>
      </div>
      <div class="col-md-3">
        <div class="modern-card p-3 text-center">
          <div class="metric-icon mb-2">
            <i class="bi bi-graph-up text-success" style="font-size: 2rem;"></i>
          </div>
          <h3 class="fw-bold text-success">{{ averageScore }}%</h3>
          <p class="text-muted mb-0">Average Score</p>
        </div>
      </div>
      <div class="col-md-3">
        <div class="modern-card p-3 text-center">
          <div class="metric-icon mb-2">
            <i class="bi bi-check-circle-fill text-info" style="font-size: 2rem;"></i>
          </div>
          <h3 class="fw-bold text-info">{{ completionRate }}%</h3>
          <p class="text-muted mb-0">Completion Rate</p>
        </div>
      </div>
      <div class="col-md-3">
        <div class="modern-card p-3 text-center">
          <div class="metric-icon mb-2">
            <i class="bi bi-clock-fill text-warning" style="font-size: 2rem;"></i>
          </div>
          <h3 class="fw-bold text-warning">{{ averageTimeSpent }}h</h3>
          <p class="text-muted mb-0">Avg. Time Spent</p>
        </div>
      </div>
    </div>

    <!-- Charts Row -->
    <div class="row mb-4">
      <!-- Performance Trends -->
      <div class="col-lg-8">
        <div class="modern-card p-4">
          <h5 class="fw-bold mb-3">Performance Trends</h5>
          <div class="chart-container">
            <canvas id="performanceChart" width="400" height="200" ref="performanceCanvas"></canvas>
          </div>
        </div>
      </div>
      
      <!-- Subject Distribution -->
      <div class="col-lg-4">
        <div class="modern-card p-4">
          <h5 class="fw-bold mb-3">Subject Performance</h5>
          <div class="subject-performance">
            <div 
              v-for="(subject, sIdx) in subjectPerformance" 
              :key="sIdx"
              class="subject-item mb-3"
            >
              <div class="d-flex justify-content-between align-items-center mb-1">
                <span class="fw-semibold">{{ subject.name }}</span>
                <span class="text-muted">{{ subject.score }}%</span>
              </div>
              <div class="progress">
                <div 
                  class="progress-bar"
                  :style="{ width: subject.score + '%', backgroundColor: subject.color }"
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Student Performance Table -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="modern-card p-4">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h5 class="fw-bold mb-0">Individual Student Performance</h5>
            <div class="input-group" style="width: 300px;">
              <span class="input-group-text">
                <i class="bi bi-search"></i>
              </span>
              <input 
                type="text" 
                class="form-control" 
                placeholder="Search students..."
                v-model="studentSearchQuery"
              >
            </div>
          </div>
          
          <div class="table-responsive">
            <table class="table table-hover">
              <thead>
                <tr>
                  <th @click="changeSort('name')" role="button">Student <i :class="sortIcon('name')"></i></th>
                  <th @click="changeSort('overallScore')" role="button">Overall Score <i :class="sortIcon('overallScore')"></i></th>
                  <th @click="changeSort('assignmentsCompleted')" role="button">Assignments Completed <i :class="sortIcon('assignmentsCompleted')"></i></th>
                  <th @click="changeSort('timeSpent')" role="button">Time Spent <i :class="sortIcon('timeSpent')"></i></th>
                  <th @click="changeSort('badgesEarned')" role="button">Badges Earned <i :class="sortIcon('badgesEarned')"></i></th>
                  <th @click="changeSort('improvement')" role="button">Improvement <i :class="sortIcon('improvement')"></i></th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(student, sIndex) in filteredStudents" :key="sIndex">
                  <td>
                    <div class="d-flex align-items-center">
                      <div class="student-avatar me-3">
                        {{ (student.name || '').charAt(0) }}
                      </div>
                      <div>
                        <div class="fw-bold">{{ student.name || 'Student' }}</div>
                        <small class="text-muted">{{ student.grade || '' }}</small>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div class="d-flex align-items-center">
                      <span class="fw-bold me-2" :class="getScoreClass(student.overallScore)">
                        {{ student.overallScore }}%
                      </span>
                      <div class="progress" style="width: 60px; height: 6px;">
                        <div 
                          class="progress-bar"
                          :style="{ width: student.overallScore + '%' }"
                          :class="getScoreProgressClass(student.overallScore)"
                        ></div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span class="fw-bold">{{ student.assignmentsCompleted }}</span>
                    <span class="text-muted">/ {{ student.totalAssignments }}</span>
                  </td>
                  <td>{{ student.timeSpent }}h</td>
                  <td>
                    <div class="d-flex align-items-center">
                      <i class="bi bi-award-fill text-warning me-1"></i>
                      <span>{{ student.badgesEarned }}</span>
                    </div>
                  </td>
                  <td>
                    <span 
                      class="badge"
                      :class="getImprovementBadgeClass(student.improvement)"
                    >
                      <i :class="getImprovementIcon(student.improvement)" class="me-1"></i>
                      {{ student.improvement > 0 ? '+' : '' }}{{ student.improvement }}%
                    </span>
                  </td>
                  <td>
                    <div class="btn-group btn-group-sm">
                      <button 
                        class="btn btn-outline-primary"
                        @click="viewStudentDetail(student)"
                        title="View Details"
                      >
                        <i class="bi bi-eye"></i>
                      </button>
                      <button 
                        class="btn btn-outline-info"
                        @click="sendMessage(student)"
                        title="Send Message"
                      >
                        <i class="bi bi-chat"></i>
                      </button>
                      <button 
                        class="btn btn-outline-success"
                        @click="assignTask(student)"
                        title="Assign Task"
                      >
                        <i class="bi bi-plus"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="d-flex justify-content-between align-items-center mt-3">
            <div class="text-muted small">Page {{ page }} of {{ Math.max(1, Math.ceil(totalRows / pageSize)) }}</div>
            <div class="btn-group">
              <button class="btn btn-sm btn-outline-secondary" :disabled="page<=1" @click="page--; loadAnalytics()">Prev</button>
              <button class="btn btn-sm btn-outline-secondary" :disabled="page>=Math.ceil(totalRows/pageSize)" @click="page++; loadAnalytics()">Next</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Insights and Recommendations -->
    <div class="row">
      <div class="col-lg-6">
        <div class="modern-card p-4">
          <h5 class="fw-bold mb-3">
            <i class="bi bi-lightbulb text-warning me-2"></i>
            Insights
          </h5>
          <div class="insights-list">
            <div 
              v-for="insight in insights" 
              :key="insight.id"
              class="insight-item p-3 mb-3 rounded"
              :class="getInsightClass(insight.type)"
            >
              <div class="d-flex align-items-start">
                <i :class="insight.icon" class="me-3 mt-1"></i>
                <div>
                  <h6 class="fw-bold mb-1">{{ insight.title }}</h6>
                  <p class="mb-0 small">{{ insight.description }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div class="col-lg-6">
        <div class="modern-card p-4">
          <h5 class="fw-bold mb-3">
            <i class="bi bi-target text-primary me-2"></i>
            Recommendations
          </h5>
          <div class="recommendations-list">
            <div 
              v-for="recommendation in recommendations" 
              :key="recommendation.id"
              class="recommendation-item p-3 mb-3 rounded bg-light"
            >
              <div class="d-flex align-items-start">
                <i class="bi bi-arrow-right-circle text-primary me-3 mt-1"></i>
                <div>
                  <h6 class="fw-bold mb-1">{{ recommendation.title }}</h6>
                  <p class="mb-2 small">{{ recommendation.description }}</p>
                  <button 
                    class="btn btn-sm btn-primary"
                    @click="implementRecommendation(recommendation)"
                  >
                    {{ recommendation.action }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'StudentAnalytics',
  data() {
    return {
      selectedTimeframe: 'month',
      selectedClass: '',
      studentSearchQuery: '',
      totalStudents: 0,
      averageScore: 0,
      completionRate: 0,
      averageTimeSpent: 0,
      subjectPerformance: [],
      students: [],
      teacherClasses: [],
      page: 1,
      pageSize: 10,
      totalRows: 0,
      sortBy: 'name',
      sortDir: 'asc',
      insights: [],
      recommendations: []
    }
  },
  computed: {
    filteredStudents() {
      if (!this.studentSearchQuery) return this.students
      return this.students.filter(student =>
        (student.name || '').toLowerCase().includes(this.studentSearchQuery.toLowerCase())
      )
    }
  },
  async mounted() {
    await this.loadTeacherClasses()
    await this.loadAnalytics()
    await this.loadTrendsChart()
  },
  methods: {
    async reloadAll() {
      this.page = 1
      await this.loadAnalytics()
      await this.loadTrendsChart()
    },
    async loadTeacherClasses() {
      try {
        const { getTeacherClasses } = await import('../services/teacherService.js')
        const user = JSON.parse(localStorage.getItem('currentUser') || 'null')
        const teacherId = user?.id || 0
        const res = await getTeacherClasses(teacherId)
        this.teacherClasses = Array.isArray(res?.data || res) ? (res.data || res) : []
      } catch (e) {
        console.error('Failed to load classes:', e)
      }
    },
    async loadAnalytics() {
      try {
        const { getTeacherAnalytics } = await import('../services/teacherService.js')
        const user = JSON.parse(localStorage.getItem('currentUser') || 'null')
        const teacherId = user?.id || 0
        const res = await getTeacherAnalytics(teacherId, {
          timeframe: this.selectedTimeframe,
          class_code: this.selectedClass || undefined,
          page: this.page,
          page_size: this.pageSize,
          sort_by: this.sortBy,
          sort_dir: this.sortDir
        })
        if (res?.success) {
          const d = res.data
          this.totalStudents = d.totals.total_students || 0
          this.averageScore = Math.round(d.totals.average_score || 0)
          this.completionRate = Math.round(d.totals.completion_rate || 0)
          this.averageTimeSpent = d.totals.average_time_spent || 0
          this.subjectPerformance = Array.isArray(d.subjectPerformance) ? d.subjectPerformance : []
          this.students = Array.isArray(d.students) ? d.students : []
          this.totalRows = d.pagination?.total || this.students.length
          // Simple insights/recommendations based on data
          this.insights = []
          if (this.averageScore >= 85) {
            this.insights.push({ id: 1, type: 'positive', icon: 'bi bi-graph-up text-success', title: 'Strong Performance', description: 'Class average score is high this period.' })
          }
          if (this.completionRate < 60) {
            this.insights.push({ id: 2, type: 'warning', icon: 'bi bi-exclamation-triangle text-warning', title: 'Low Completion', description: 'Encourage students to submit pending work.' })
          }
          this.recommendations = [
            { id: 1, title: 'Assign Review Tasks', description: 'Help students practice weak areas.', action: 'Create Tasks' },
            { id: 2, title: 'Celebrate Achievements', description: 'Reward students with badges for progress.', action: 'Send Badges' }
          ]
        }
      } catch (e) {
        console.error('Failed to load teacher analytics:', e)
      }
    },
    async loadTrendsChart() {
      try {
        const { getTeacherAnalyticsTrends } = await import('../services/teacherService.js')
        const user = JSON.parse(localStorage.getItem('currentUser') || 'null')
        const teacherId = user?.id || 0
        const res = await getTeacherAnalyticsTrends(teacherId, { weeks: 8, class_code: this.selectedClass || undefined })
        const data = res?.success ? res.data : []
        // Defer rendering to ensure canvas ref exists
        this.$nextTick(() => {
          this.renderChartJs(data)
        })
      } catch (e) {
        console.error('Failed to load trends:', e)
      }
    },
    async renderChartJs(data) {
      try {
        if (!window.Chart) {
          await new Promise((resolve, reject) => {
            const s = document.createElement('script')
            s.src = 'https://cdn.jsdelivr.net/npm/chart.js'
            s.onload = resolve
            s.onerror = reject
            document.head.appendChild(s)
          })
        }
        const canvas = this.$refs.performanceCanvas
        if (!canvas) return
        if (this._chart) {
          this._chart.destroy()
        }
        const labels = data.map(d => d.weekStart)
        const values = data.map(d => d.averageScore)
        const ctx = canvas.getContext('2d')
        this._chart = new window.Chart(ctx, {
          type: 'line',
          data: {
            labels,
            datasets: [{
              label: 'Average Score',
              data: values,
              borderColor: '#3b82f6',
              backgroundColor: 'rgba(59, 130, 246, 0.2)',
              tension: 0.3,
              pointRadius: 3
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            scales: { y: { beginAtZero: true, max: 100 } },
            plugins: { legend: { display: false } }
          }
        })
      } catch (e) {
        console.error('Chart.js load/render failed:', e)
      }
    },

    getScoreClass(score) {
      if (score >= 90) return 'text-success'
      if (score >= 80) return 'text-primary'
      if (score >= 70) return 'text-warning'
      return 'text-danger'
    },

    getScoreProgressClass(score) {
      if (score >= 90) return 'bg-success'
      if (score >= 80) return 'bg-primary'
      if (score >= 70) return 'bg-warning'
      return 'bg-danger'
    },

    getImprovementBadgeClass(improvement) {
      if (improvement > 0) return 'bg-success'
      if (improvement < 0) return 'bg-danger'
      return 'bg-secondary'
    },

    getImprovementIcon(improvement) {
      if (improvement > 0) return 'bi bi-arrow-up'
      if (improvement < 0) return 'bi bi-arrow-down'
      return 'bi bi-dash'
    },

    getInsightClass(type) {
      const classes = {
        positive: 'bg-success bg-opacity-10 border border-success',
        warning: 'bg-warning bg-opacity-10 border border-warning',
        info: 'bg-info bg-opacity-10 border border-info'
      }
      return classes[type] || 'bg-light'
    },

    viewStudentDetail(student) {
      this.$router.push(`/student/${student.id}`)
    },

    sendMessage(student) {
      this.showMessage(`Sending message to ${student.name}`)
    },

    assignTask(student) {
      this.showMessage(`Assigning task to ${student.name}`)
    },

    implementRecommendation(recommendation) {
      this.showMessage(`Implementing: ${recommendation.title}`)
    },

    // Sorting handlers for table headers
    changeSort(key) {
      if (this.sortBy === key) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortBy = key
        this.sortDir = 'asc'
      }
      this.page = 1
      this.loadAnalytics()
    },
    sortIcon(key) {
      if (this.sortBy !== key) return 'bi bi-arrow-down-up text-muted'
      return this.sortDir === 'asc' ? 'bi bi-arrow-up-short' : 'bi bi-arrow-down-short'
    },

    exportReport() {
      // Export the analytics table to CSV, fetching all pages server-side
      // so we include all students matching current filters
      this.exportAnalyticsCsv().catch((e) => {
        console.error('Export failed:', e)
        this.showMessage('Export failed')
      })
    },

    showMessage(message, type = 'info') {
      // Create feedback alert
      const alertClass = type === 'success' ? 'alert-success' : 
                         type === 'warning' ? 'alert-warning' :
                         type === 'error' ? 'alert-danger' : 'alert-info';
      const alert = document.createElement('div');
      alert.className = `alert ${alertClass} alert-dismissible fade show position-fixed`;
      alert.style.cssText = 'top: 20px; right: 20px; z-index: 9999; min-width: 300px;';
      alert.innerHTML = `
        <strong>${message}</strong>
        <button type="button" class="btn-close" onclick="this.parentElement.remove()"></button>
      `;
      document.body.appendChild(alert);
      
      // Auto-remove after 5 seconds
      setTimeout(() => {
        if (alert.parentElement) {
          alert.remove();
        }
      }, 5000);
    },

      async exportAnalyticsCsv() {
        // Build parameters using current UI filters
        const { getTeacherAnalytics } = await import('../services/teacherService.js')
        const user = JSON.parse(localStorage.getItem('currentUser') || 'null')
        const teacherId = user?.id || 0

        const params = {
          timeframe: this.selectedTimeframe,
          class_code: this.selectedClass || undefined,
          page: 1,
          page_size: 200, // backend max safeguard
          sort_by: this.sortBy,
          sort_dir: this.sortDir
        }

        // Helper to fetch a page
        const fetchPage = async (page) => {
          const res = await getTeacherAnalytics(teacherId, { ...params, page })
          if (!res?.success) throw new Error('Failed to fetch analytics for export')
          return res.data
        }

        // Fetch first page to get total
        const first = await fetchPage(1)
        let allRows = Array.isArray(first.students) ? first.students.slice() : []
        const total = first.pagination?.total || allRows.length
        const pageSize = first.pagination?.page_size || params.page_size
        let nextPage = 2
        while (allRows.length < total) {
          const d = await fetchPage(nextPage)
          allRows = allRows.concat(d.students || [])
          nextPage += 1
          if ((nextPage - 1) * pageSize > total + pageSize) break // safety
        }

        // Convert to CSV
        const headers = [
          'Student',
          'Grade',
          'Overall Score',
          'Assignments Completed',
          'Total Assignments',
          'Time Spent (h)',
          'Badges Earned',
          'Improvement (%)'
        ]
        const escapeCsv = (val) => {
          const s = String(val ?? '')
          if (/[",\n]/.test(s)) return '"' + s.replace(/"/g, '""') + '"'
          return s
        }
        const lines = [headers.join(',')]
        for (const r of allRows) {
          lines.push([
            escapeCsv(r.name),
            escapeCsv(r.grade),
            escapeCsv(r.overallScore),
            escapeCsv(r.assignmentsCompleted),
            escapeCsv(r.totalAssignments),
            escapeCsv(r.timeSpent),
            escapeCsv(r.badgesEarned),
            escapeCsv(r.improvement)
          ].join(','))
        }
        const csv = lines.join('\n')

        // Trigger download
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        const classPart = this.selectedClass ? `-${this.selectedClass}` : ''
        a.href = url
        a.download = `student-analytics${classPart}-${this.selectedTimeframe}.csv`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
      }
  }
}
</script>

<style scoped>
.student-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--role-primary);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
}

.chart-container {
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-secondary);
  border-radius: 8px;
}

.subject-item {
  transition: all 0.2s ease;
}

.subject-item:hover {
  transform: translateX(5px);
}

.progress {
  height: 8px;
  background: var(--bg-secondary);
}

.insight-item,
.recommendation-item {
  transition: all 0.2s ease;
}

.insight-item:hover,
.recommendation-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.table th {
  border-top: none;
  font-weight: 600;
  color: var(--text-primary);
}

.table td {
  vertical-align: middle;
}

.btn-group-sm .btn {
  padding: 0.25rem 0.5rem;
}
</style> 